import { Document, Page, Text } from '@react-pdf/renderer'
import { artifactSiteLine } from 'features/knowledgeDocumentViewer/artifacts/artifactDate'
import { parseMarkdownBlocks } from 'features/knowledgeDocumentViewer/artifacts/freeformMarkdown'
import { tokenizeMissingMarkers } from 'features/knowledgeDocumentViewer/artifacts/missingMarkers'
import type { DocumentArtifact } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import React, { useMemo } from 'react'

import { PdfBlock } from './PdfBlock'
import { PDF_FONTS, type PdfFontSet } from './pdfFonts'
import { PdfFooter } from './PdfFooter'
import { createPdfStyles } from './PdfPrimitives'
import type { ArtifactPdfSource } from './RenderArtifactPdfBlob'

/** Letter-portrait PDF of the freeform document kind in the sheet typography. */
export function FreeformDocumentPdf({
  artifact,
  fonts = PDF_FONTS.helvetica,
}: {
  artifact: DocumentArtifact
  fonts?: PdfFontSet
}): React.ReactElement {
  const styles = useMemo(() => createPdfStyles(fonts), [fonts])
  return (
    <Document title={artifact.title}>
      <Page size='LETTER' style={styles.page}>
        <Text style={styles.title}>{artifact.title}</Text>
        <Text style={styles.titleMeta}>{artifactSiteLine(artifact)}</Text>
        {artifact.content.sections.map((section) => (
          <React.Fragment key={section.section_id}>
            <Text style={styles.sectionHeading} minPresenceAhead={36}>
              {section.heading}
            </Text>
            {parseMarkdownBlocks(tokenizeMissingMarkers(section.body)).map(
              (block, index) => (
                <PdfBlock key={index} block={block} styles={styles} />
              )
            )}
          </React.Fragment>
        ))}
        <PdfFooter label={artifact.title} styles={styles} />
      </Page>
    </Document>
  )
}

/** Only what the page prints decides the font: title, site line, headings, bodies; provenance and open items never reach the PDF. */
export function documentPdfText(artifact: DocumentArtifact): string {
  return [
    artifact.title,
    artifactSiteLine(artifact),
    ...artifact.content.sections.flatMap((section) => [
      section.heading,
      section.body,
    ]),
  ].join('\n')
}

export function documentPdfSource(
  artifact: DocumentArtifact
): ArtifactPdfSource {
  return {
    text: documentPdfText(artifact),
    element: (fonts) => (
      <FreeformDocumentPdf artifact={artifact} fonts={fonts} />
    ),
  }
}
